import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Linkedin, Youtube, Mail, Phone, MapPin, Send, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { LOGO_URL, CORPORATE_CATEGORIES, PERSONAL_CATEGORIES } from '../mock/mock';
import { submitSubscription } from '../lib/api';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await submitSubscription(email);
      setSuccess(true);
      setEmail('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#2C2416] text-[#F4E7DA]">
      {/* Newsletter */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-14 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="font-serif-display text-3xl md:text-4xl leading-tight">Join our gifting circle</h3>
            <p className="text-sm text-[#D9CBB4] mt-3 max-w-md">Fresh hamper drops, festive edits and thoughtful ideas - delivered a few times a season, never more.</p>
          </div>
          <div className="flex flex-col gap-2 w-full">
            <form onSubmit={handleSubscribe} className="flex gap-2 w-full">
              <input 
                type="email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com" 
                required
                disabled={loading}
                className="flex-1 bg-transparent border border-white/25 rounded-full px-5 py-3 text-sm placeholder:text-white/40 focus:outline-none focus:border-[#F4D5C5] transition-colors disabled:opacity-50" 
              />
              <button 
                type="submit"
                disabled={loading}
                className="bg-[#F4D5C5] text-[#2C2416] rounded-full px-6 text-sm font-medium flex items-center gap-2 hover:bg-[#F5C6A5] transition-colors disabled:opacity-50 min-w-[130px] justify-center"
              >
                {loading ? (
                  <>Subscribing <Loader2 className="w-4 h-4 animate-spin" /></>
                ) : (
                  <>Subscribe <Send className="w-4 h-4" /></>
                )}
              </button>
            </form>
            {success && (
              <p className="text-xs text-[#F4D5C5] flex items-center gap-1.5 mt-1">
                <CheckCircle className="w-3.5 h-3.5 text-[#F4D5C5]" /> Thank you for subscribing!
              </p>
            )}
            {error && (
              <p className="text-xs text-red-300 flex items-center gap-1.5 mt-1">
                <AlertCircle className="w-3.5 h-3.5" /> {error}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-14 grid md:grid-cols-2 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <div className="inline-block">
            <img src={LOGO_URL} alt="Wrap Your Wish" className="h-14 w-auto object-contain" />
          </div>
          <p className="text-sm text-[#D9CBB4] mt-5 max-w-md leading-relaxed">Wrap Your Wish is a premium gifting studio in India - crafting thoughtful hampers for teams that care about people and brands that care about relationships.</p>
          <div className="flex gap-3 mt-6">
            <a href="https://www.instagram.com/the_wrapyourwish?utm_source=qr&igsi=c21oeTR2cGRwNHp4" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:bg-[#F4D5C5] hover:text-[#2C2416] hover:border-[#F4D5C5] transition-colors" aria-label="Instagram">
              <Instagram className="w-4 h-4" />
            </a>
            <a href="https://www.facebook.com/share/1FgCwpF7yn/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:bg-[#F4D5C5] hover:text-[#2C2416] hover:border-[#F4D5C5] transition-colors" aria-label="Facebook">
              <Facebook className="w-4 h-4" />
            </a>
            <a href="https://www.linkedin.com/in/wrap-your-wish-a-corporate-gifting-company-704b51133?utm_source=share_via&utm_content=profile&utm_medium=member_android" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:bg-[#F4D5C5] hover:text-[#2C2416] hover:border-[#F4D5C5] transition-colors" aria-label="LinkedIn">
              <Linkedin className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div>
          <h5 className="text-xs uppercase tracking-[0.2em] text-white/60 mb-4">Corporate</h5>
          <ul className="space-y-2 text-sm">
            {CORPORATE_CATEGORIES.slice(0, 6).map(c => (
              <li key={c.id}><Link to={`/category/corporate/${c.id}`} className="text-[#D9CBB4] hover:text-[#F4D5C5] transition-colors">{c.title}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <h5 className="text-xs uppercase tracking-[0.2em] text-white/60 mb-4">Personal</h5>
          <ul className="space-y-2 text-sm">
            {PERSONAL_CATEGORIES.map(c => (
              <li key={c.id}><Link to={`/category/personal/${c.id}`} className="text-[#D9CBB4] hover:text-[#F4D5C5] transition-colors">{c.title}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <h5 className="text-xs uppercase tracking-[0.2em] text-white/60 mb-4">Get in Touch</h5>
          <ul className="space-y-3 text-sm text-[#D9CBB4]">
            <li className="flex gap-2"><Phone className="w-4 h-4 mt-0.5 text-[#F4D5C5]" /> +91 88605 53236</li>
            <li className="flex gap-2"><Mail className="w-4 h-4 mt-0.5 text-[#F4D5C5]" /> reachus@wrapyourwish.com</li>
            <li className="flex gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[#F4D5C5]" /> Rajendra Nagar Industrial Area,<br/>Ghaziabad, UP, India</li>
          </ul>
        </div>
      </div>

      {/* Client Success Stories & Case Studies */}
      <div className="border-t border-white/10 bg-black/10">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h6 className="text-[11px] uppercase tracking-[0.2em] text-white/50 mb-2 font-semibold">
                Client Success Stories &amp; Case Studies
              </h6>
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-[#D9CBB4]/80">
                <Link to="/case-studies/delivering-custom-diwali-hampers-delhi" className="hover:text-[#F4D5C5] transition-colors">
                  Diwali Hampers Rush Delivery Delhi NCR
                </Link>
                <span className="text-white/20">·</span>
                <Link to="/case-studies/year-round-gifting-remote-first-startup" className="hover:text-[#F4D5C5] transition-colors">
                  Automated Remote Team Milestone Gifting
                </Link>
                <span className="text-white/20">·</span>
                <Link to="/case-studies/client-retention-corporate-gifting-ca-firm" className="hover:text-[#F4D5C5] transition-colors">
                  CA Firm Client Retention &amp; Luxury Tier Hampers
                </Link>
              </div>
            </div>
            <Link
              to="/case-studies"
              className="text-xs uppercase tracking-widest text-[#F4D5C5] hover:text-white font-semibold shrink-0"
            >
              Browse All Stories →
            </Link>
          </div>
        </div>
      </div>

      {/* Corporate Hubs & Popular Searches for SEO Silos */}
      <div className="border-t border-white/10 bg-black/20">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <h6 className="text-[11px] uppercase tracking-[0.2em] text-white/50 mb-3 font-semibold">
            Corporate Gifting Hubs &amp; Popular Searches
          </h6>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-[#D9CBB4]/80">
            <Link to="/corporate-gifting-delhi-ncr" className="hover:text-[#F4D5C5] transition-colors">
              Corporate Gifting Companies in Delhi NCR
            </Link>
            <span className="text-white/20">·</span>
            <Link to="/corporate-gifting-gurgaon" className="hover:text-[#F4D5C5] transition-colors">
              Corporate Gifting Gurgaon
            </Link>
            <span className="text-white/20">·</span>
            <Link to="/corporate-gifts-bangalore" className="hover:text-[#F4D5C5] transition-colors">
              Corporate Gifts Bangalore
            </Link>
            <span className="text-white/20">·</span>
            <Link to="/employee-welcome-kits" className="hover:text-[#F4D5C5] transition-colors">
              Employee Onboarding Kit Suppliers India
            </Link>
            <span className="text-white/20">·</span>
            <Link to="/client-gifting" className="hover:text-[#F4D5C5] transition-colors">
              Bulk Client Gift Hampers
            </Link>
            <span className="text-white/20">·</span>
            <Link to="/festive-hampers" className="hover:text-[#F4D5C5] transition-colors">
              Corporate Diwali Gifts for Employees
            </Link>
            <span className="text-white/20">·</span>
            <Link to="/wedding-return-gifts" className="hover:text-[#F4D5C5] transition-colors">
              Wedding Return Gifts Luxury Bulk
            </Link>
            <span className="text-white/20">·</span>
            <Link to="/occasion-gifting" className="hover:text-[#F4D5C5] transition-colors">
              Personalized Baby Shower Hampers
            </Link>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/50">
          <div className="flex flex-col md:flex-row items-center gap-3 md:gap-8">
            <p>© {new Date().getFullYear()} Wrap Your Wish. All rights reserved.</p>
            <div className="flex gap-5">
              <Link to="/terms" className="hover:text-white">Terms</Link>
              <Link to="/refund" className="hover:text-white">Refund</Link>
            </div>
          </div>
          <div className="hidden md:block w-32" />
        </div>
      </div>
    </footer>
  );
};

export default Footer;
